import _ from 'lodash';

import api from '../../api';
import worldMap from '../../core/map';
import translate from '../../core/tools/translate';
import { notify, NOTIFY_TYPE, loading, unloading } from '../../shared';
import { ServiceType } from '../../../enuns/AfterSales/serviceArea.enum';
import colorbrewer from '../../core/tools/colorbrewer';
import ColorFlatUI from '../../core/tools/colorflatui';
import databoard from '../../core/databoard';
import { CoreControl } from '../CoreControl';
import { VectorType } from '../VectorType';

const LayerControls = (name) => require('../../controls').get(name);

const GetLayer = (name, currentMap = worldMap.getCurrent()) => {
    return require('../../layers/index').default.get(name, currentMap);
};

export class ServiceAreaAfterSalesControl extends CoreControl {
    constructor () {
        super({ name: 'ServiceAreaAfterSales' });

        this._change = this._change.bind(this);
        this.purge = this.purge.bind(this);
        this._toggleShowRadiusReal = this._toggleShowRadiusReal.bind(this);
        this._toggleShowRadiusTheoretical = this._toggleShowRadiusTheoretical.bind(this);
        this._toggleShowLabelRadius = this._toggleShowLabelRadius.bind(this);
        this.setFillColor = this.setFillColor.bind(this);
        this.setLineColor = this.setLineColor.bind(this);
        this.setLineWeight = this.setLineWeight.bind(this);
        this.setOpacity = this.setOpacity.bind(this);
        this.toggleAll = this.toggleAll.bind(this);

        this._syncing = false;
        this._instance = null;
        this._customStoreStyles = {};
    }

    static getInstance () {
        if (!ServiceAreaAfterSalesControl._instance) {
            ServiceAreaAfterSalesControl._instance = new ServiceAreaAfterSalesControl();
        }

        return ServiceAreaAfterSalesControl._instance;
    }

    init () {
        const values = {
            disabled: true,
            help: translate(`Help.${this.options.name}`),
            id: 'layer-tradearea',
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            purge: this.purge,
            type: 'vector-layers-service-area',
            fillColor: {
                name: 'fillColor',
                change: this.setFillColor
            },
            lineWeight: {
                change: this.setLineWeight
            },
            opacity: {
                change: this.setOpacity
            },
            clearPaint: () => this._clearAllStoreAreas(),
            checkbox: {}
        };

        values.checkbox['Gerar Área Real'] = {
            name: 'showRadiusReal',
            change: this._toggleShowRadiusReal,
            value: true
        };

        values.checkbox['Gerar Área Potencial'] = {
            name: 'showRadiusTheoretical',
            change: this._toggleShowRadiusTheoretical,
            value: ''
        };

        values.checkbox['Mostrar Label do Raio em KM'] = {
            name: 'showLabelRadius',
            change: this._toggleShowLabelRadius,
            value: ''
        };

        values.checkbox[translate('Action.LoadFromAllVisibleStores')] = {
            name: 'loadFromAllVisibleStores',
            change: this.toggleAll,
            value: ''
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    _isNetworkActive (mapId) {
        try {
            const networkControls = LayerControls('NetworkAfterSales').getControls(mapId);
            return networkControls && networkControls.switch && networkControls.switch.onOff && networkControls.switch.onOff.status;
        } catch (err) {
            return false;
        }
    }

    _getServiceAreaOptions () {
        const controls = this.getControls();

        let color = colorbrewer['Default'][4];
        let opacity = 0.1;
        let weight = 2;

        try {
            if (controls.colorpicker && controls.colorpicker.fillColor) {
                const ref = controls.colorpicker.fillColor.value || 'Default:4';
                const [name, idx] = ref.split(':');
                color = (colorbrewer[name] && colorbrewer[name][idx]) || color;
            }
            if (controls.slider && controls.slider.opacity) {
                opacity = (controls.slider.opacity.value || 1) / 10;
            }
            if (controls.slider && controls.slider.lineWeight) {
                weight = controls.slider.lineWeight.value || 2;
            }
        } catch (err) {}

        return {
            showRadiusReal: controls.checkbox.showRadiusReal ? controls.checkbox.showRadiusReal.status : true,
            showRadiusTheoretical: controls.checkbox.showRadiusTheoretical ? controls.checkbox.showRadiusTheoretical.status : false,
            showLabelRadius: controls.checkbox.showLabelRadius ? controls.checkbox.showLabelRadius.status : false,
            color,
            opacity,
            weight
        };
    }

    _layerHasFeatures (layer) {
        try { return layer.getFeatures().length > 0; } catch (e) { return false; }
    }

    _findColorRef (colorArray) {
        const target = JSON.stringify(colorArray);
        const entries = Object.entries(colorbrewer);
        for (let i = 0; i < entries.length; i++) {
            const [name, themes] = entries[i];
            if (!_.isObject(themes)) continue;
            const ranges = Object.entries(themes);
            for (let j = 0; j < ranges.length; j++) {
                if (JSON.stringify(ranges[j][1]) === target) return `${name}:${ranges[j][0]}`;
            }
        }
        return null;
    }

    syncStyles (options) {
        try {
            const controls = this.getControls();

            if (options.color && controls.colorpicker && controls.colorpicker.fillColor) {
                const ref = this._findColorRef(options.color);
                if (ref) {
                    controls.colorpicker.fillColor.value = ref;
                    controls.colorpicker.fillColor.input.value(ref);
                }
            }

            if (options.opacity !== undefined && controls.slider && controls.slider.opacity) {
                const sliderVal = Math.round(options.opacity * 10);
                controls.slider.opacity.value = sliderVal;
                controls.slider.opacity.input.value(sliderVal);
            }

            if (options.weight !== undefined && controls.slider && controls.slider.lineWeight) {
                controls.slider.lineWeight.value = options.weight;
                controls.slider.lineWeight.input.value(options.weight);
            }
        } catch (err) {}
    }

    syncCheckboxes (options) {
        this._syncing = true;
        try {
            const controls = this.getControls();

            if (controls.checkbox.showRadiusReal) {
                const val = !!options.showRadiusReal || this._layerHasFeatures(this._getRealLayer());
                controls.checkbox.showRadiusReal.status = val;
                controls.checkbox.showRadiusReal.input.iCheck(val ? 'check' : 'uncheck');
            }
            if (controls.checkbox.showRadiusTheoretical) {
                const val = !!options.showRadiusTheoretical || this._layerHasFeatures(this._getTheoreticalLayer());
                controls.checkbox.showRadiusTheoretical.status = val;
                controls.checkbox.showRadiusTheoretical.input.iCheck(val ? 'check' : 'uncheck');
            }
            if (controls.checkbox.showLabelRadius && options.showLabelRadius !== undefined) {
                const val = !!options.showLabelRadius;
                controls.checkbox.showLabelRadius.status = val;
                controls.checkbox.showLabelRadius.input.iCheck(val ? 'check' : 'uncheck');
            }
        } catch (err) {}
        this._syncing = false;
    }

    _getRealLayer () {
        return GetLayer('ServiceAreaNetworkAfterSales');
    }

    _getTheoreticalLayer () {
        return GetLayer('ServiceAreaTheoreticalNetworkAfterSales');
    }

    _change (e) {
        if (e.checked) {
            const controls = this.getControls();
            const isToggleAll = controls.checkbox.loadFromAllVisibleStores
                && controls.checkbox.loadFromAllVisibleStores.status;

            if (isToggleAll) {
                this._loadLayers();
            }
        } else {
            this._hideAllLayers();
        }
    }

    toggleAll (checked) {
        if (!this.check()) {
            return;
        }

        if (checked) {
            this._loadLayers();
        } else {
            this._hideAllLayers();
        }
    }

    _loadLayers () {
        if (!this.validToBuildServiceArea()) {
            return;
        }

        this._hideAllLayers();
        const options = this._getServiceAreaOptions();

        if (options.showRadiusReal) {
            const realLayer = this._getRealLayer();
            realLayer._customStoreStyles = this._customStoreStyles;
            this._enableRemote(realLayer);
            realLayer.setStyle(options, false);
            realLayer.redraw();
        }

        if (options.showRadiusTheoretical) {
            const theoreticalLayer = this._getTheoreticalLayer();
            theoreticalLayer._customStoreStyles = this._customStoreStyles;
            this._enableRemote(theoreticalLayer);
            theoreticalLayer.setStyle(options, false);
            theoreticalLayer.redraw();
        }
    }

    _updateInfoPanel () {
        try {
            const $content = $('.store-influence-card-header');
            if (!$content.length) return;

            const options = this._getServiceAreaOptions();
            const hasReal = options.showRadiusReal && this._layerHasFeatures(this._getRealLayer());
            const hasTheoretical = options.showRadiusTheoretical && this._layerHasFeatures(this._getTheoreticalLayer());

            if (hasReal || hasTheoretical) {
                const nameTypeArea = (hasReal && hasTheoretical) ? 'Área Real + Área Potencial'
                    : hasReal ? 'Área Real' : 'Área Potencial';
                $content.find('.store-influence-btn-edit').text('Editar');
                $content.find('.store-influence-btn-delete').show();
                $content.find('.store-influence-subtitle').text(nameTypeArea);
            } else {
                $content.find('.store-influence-btn-edit').text('Gerar');
                $content.find('.store-influence-btn-delete').hide();
                $content.find('.store-influence-subtitle').text('A definir');
            }
        } catch (err) {}
    }

    _isToggleAllActive () {
        try {
            const controls = this.getControls();
            return controls.checkbox.loadFromAllVisibleStores
                && controls.checkbox.loadFromAllVisibleStores.status;
        } catch (err) {
            return false;
        }
    }

    _getStoreIdsFromLayer (layer) {
        try {
            const features = layer.getFeatures();
            return _(features)
                .map(f => f.properties.StoreId || f.properties.IdLoja)
                .compact()
                .uniq()
                .value();
        } catch (e) {
            return [];
        }
    }

    _loadAreaTypeForStores (storeIds, serviceType, layer, options) {
        if (!storeIds.length) return;

        const bothActive = options.showRadiusReal && options.showRadiusTheoretical;

        loading();
        layer.setStyle(options, false);

        const requests = storeIds.map(storeId =>
            api.afterSales.get.serviceArea(storeId, serviceType).then(response => {
                let features = response.features;

                if (bothActive && serviceType === ServiceType.Theoretical) {
                    features = features.map(f => ({
                        ...f,
                        properties: {
                            ...f.properties,
                            Order: f.properties.Order + 3,
                            ServiceType: 'Theoretical'
                        }
                    }));
                }

                return features;
            })
        );

        Promise.all(requests).then(results => {
            const newFeatures = _.flatten(results);
            const existing = layer.getFeatures();
            const data = {
                features: _(newFeatures).concat(existing).uniqBy('properties.Id').value(),
                type: 'FeatureCollection'
            };
            layer.setFeatures(data, true);
            unloading();
        });
    }

    _reloadTheoreticalWithOrder (theoreticalLayer, options) {
        const storeIds = this._getStoreIdsFromLayer(theoreticalLayer);
        if (!storeIds.length) return;

        theoreticalLayer.clear(true);
        this._loadAreaTypeForStores(storeIds, ServiceType.Theoretical, theoreticalLayer, options);
    }

    _toggleShowRadiusReal (checked) {
        if (this._syncing || !this.check()) {
            return;
        }

        const realLayer = this._getRealLayer();
        const theoreticalLayer = this._getTheoreticalLayer();
        const options = this._getServiceAreaOptions();

        if (checked) {
            if (!this.validToBuildServiceArea()) {
                return;
            }

            if (this._isToggleAllActive()) {
                realLayer._customStoreStyles = this._customStoreStyles;
                theoreticalLayer._customStoreStyles = this._customStoreStyles;
                this._enableRemote(realLayer);
                realLayer.setStyle(options, false);
                realLayer.redraw();
                theoreticalLayer.setStyle(options, false);
                theoreticalLayer.redraw();
            } else {
                const storeIds = this._getStoreIdsFromLayer(theoreticalLayer);
                if (storeIds.length) {
                    this._loadAreaTypeForStores(storeIds, ServiceType.Real, realLayer, options);
                    this._reloadTheoreticalWithOrder(theoreticalLayer, options);
                } else {
                    realLayer.setStyle(options, true);
                }
            }
        } else {
            realLayer.clear(true);

            if (options.showRadiusTheoretical) {
                this._reloadTheoreticalWithOrder(theoreticalLayer, options);
            }
        }

        this._updateInfoPanel();
    }

    _toggleShowRadiusTheoretical (checked) {
        if (this._syncing || !this.check()) {
            return;
        }

        const theoreticalLayer = this._getTheoreticalLayer();
        const options = this._getServiceAreaOptions();

        if (checked) {
            if (!this.validToBuildServiceArea()) {
                return;
            }

            if (this._isToggleAllActive()) {
                theoreticalLayer._customStoreStyles = this._customStoreStyles;
                this._enableRemote(theoreticalLayer);
                theoreticalLayer.setStyle(options, false);
                theoreticalLayer.redraw();
            } else {
                const storeIds = this._getStoreIdsFromLayer(this._getRealLayer());
                if (storeIds.length) {
                    this._loadAreaTypeForStores(storeIds, ServiceType.Theoretical, theoreticalLayer, options);
                } else {
                    theoreticalLayer.setStyle(options, true);
                }
            }
        } else {
            theoreticalLayer.clear(true);
        }

        this._updateInfoPanel();
    }

    _toggleShowLabelRadius (checked) {
        if (!this.check()) return;

        const options = this._getServiceAreaOptions();
        const realLayer = this._getRealLayer();
        const theoreticalLayer = this._getTheoreticalLayer();

        if (realLayer.visible()) {
            realLayer.setStyle({ ...options, showLabelRadius: checked }, true);
        }
        if (theoreticalLayer.visible()) {
            theoreticalLayer.setStyle({ ...options, showLabelRadius: checked }, true);
        }
    }

    _hideAllLayers () {
        try { this._getRealLayer().clear(true); } catch (e) {}
        try { this._getTheoreticalLayer().clear(true); } catch (e) {}

        databoard.reset();
        databoard.hide();
        this._updateInfoPanel();
    }

    setOpacity (value) {
        const opacity = value / 10;
        const options = this._getServiceAreaOptions();
        options.opacity = opacity;

        [this._getRealLayer(), this._getTheoreticalLayer()].forEach(layer => {
            if (!layer.visible()) return;
            layer.setStyle(options, true);
        });
    }

    setFillColor (value) {
        const color = value.split(':')[0];
        const index = value.split(':')[1];
        const palette = colorbrewer[color] && colorbrewer[color][index]
            ? colorbrewer[color][index]
            : colorbrewer['Default'][4];

        const options = this._getServiceAreaOptions();
        options.color = palette;

        const realLayer = this._getRealLayer();
        const theoreticalLayer = this._getTheoreticalLayer();

        if (realLayer.visible()) realLayer.setStyle(options, true);
        if (theoreticalLayer.visible()) theoreticalLayer.setStyle(options, true);
    }

    setLineColor (value) {
        const color = value.split(':')[0];
        const index = value.split(':')[1];
        const lineColor = ColorFlatUI[color] && ColorFlatUI[color][index]
            ? ColorFlatUI[color][index][0]
            : '#000000';

        const realLayer = this._getRealLayer();
        const theoreticalLayer = this._getTheoreticalLayer();

        [realLayer, theoreticalLayer].forEach(layer => {
            if (!layer.visible()) return;
            const { symbology } = layer.options;
            symbology.values.forEach(v => {
                v.style.color = lineColor;
            });
            layer.setOptions({ symbology });
            layer.reload();
        });
    }

    setLineWeight (value) {
        const options = this._getServiceAreaOptions();
        options.weight = value;

        const realLayer = this._getRealLayer();
        const theoreticalLayer = this._getTheoreticalLayer();

        if (realLayer.visible()) realLayer.setStyle(options, true);
        if (theoreticalLayer.visible()) theoreticalLayer.setStyle(options, true);
    }

    _disableRemote (layer) {
        if (!layer._savedRemote) {
            layer._savedRemote = layer.options.remote;
        }
        layer.setOptions({ remote: null });
    }

    _enableRemote (layer) {
        if (layer._savedRemote) {
            layer.setOptions({ remote: layer._savedRemote });
            delete layer._savedRemote;
        }
    }

    getStoreCustomStyle (storeId) {
        const matchStore = (f) => f.properties.StoreId === storeId || f.properties.IdLoja === storeId;
        try {
            const realFeatures = this._getRealLayer().getFeatures();
            const theoreticalFeatures = this._getTheoreticalLayer().getFeatures();
            const feature = [...realFeatures, ...theoreticalFeatures].find(f => matchStore(f) && f.properties._customStyle);
            return feature ? feature.properties._customStyle : null;
        } catch (e) {
            return null;
        }
    }

    _injectCustomStyle (features, options) {
        const customStyle = { color: options.color, opacity: options.opacity, weight: options.weight };
        return features.map(f => ({
            ...f,
            properties: { ...f.properties, _customStyle: customStyle }
        }));
    }

    loadForStore (storeId, options) {
        const realLayer = this._getRealLayer();
        const theoreticalLayer = this._getTheoreticalLayer();

        const isToggleAll = this._isToggleAllActive();

        if (!isToggleAll) {
            this._disableRemote(realLayer);
            this._disableRemote(theoreticalLayer);
        }

        if (!options.showRadiusReal && !options.showRadiusTheoretical) {
            return;
        }

        this._customStoreStyles[storeId] = { color: options.color, opacity: options.opacity, weight: options.weight };

        this.syncCheckboxes(options);

        loading();

        if (options.showRadiusReal) {
            api.afterSales.get.serviceArea(storeId, ServiceType.Real).then((response) => {
                const newFeatures = this._injectCustomStyle(response.features, options);
                const existing = realLayer.getFeatures();
                const data = {
                    features: _(newFeatures).concat(existing).uniqBy('properties.Id').value(),
                    type: 'FeatureCollection'
                };
                realLayer.setFeatures(data, true);

                if (!options.showRadiusTheoretical) {
                    unloading();
                }
            });
        }

        if (options.showRadiusTheoretical) {
            const bothActive = options.showRadiusReal && options.showRadiusTheoretical;

            api.afterSales.get.serviceArea(storeId, ServiceType.Theoretical).then((response) => {
                let features = response.features;

                if (bothActive) {
                    features = features.map(f => ({
                        ...f,
                        properties: {
                            ...f.properties,
                            Order: f.properties.Order + 3,
                            ServiceType: 'Theoretical'
                        }
                    }));
                }

                features = this._injectCustomStyle(features, options);
                const existing = theoreticalLayer.getFeatures();
                const data = {
                    features: _(features).concat(existing).uniqBy('properties.Id').value(),
                    type: 'FeatureCollection'
                };
                theoreticalLayer.setFeatures(data, true);
                unloading();
            });
        }
    }

    removeStore (storeId) {
        delete this._customStoreStyles[storeId];

        const notThisStore = (f) => f.properties.StoreId !== storeId && f.properties.IdLoja !== storeId;
        [this._getRealLayer(), this._getTheoreticalLayer()].forEach(layer => {
            try {
                const features = layer.getFeatures().filter(notThisStore);
                layer.setFeatures({ features, type: 'FeatureCollection' }, true);
            } catch (e) {}
        });
    }

    hasServiceAreaForStore (storeId) {
        const matchStore = (f) => f.properties.StoreId === storeId || f.properties.IdLoja === storeId;
        try {
            const hasReal = this._getRealLayer().getFeatures().some(matchStore);
            const hasTheoretical = this._getTheoreticalLayer().getFeatures().some(matchStore);
            return { hasReal, hasTheoretical, hasAny: hasReal || hasTheoretical };
        } catch (err) {
            return { hasReal: false, hasTheoretical: false, hasAny: false };
        }
    }

    _clearAllStoreAreas () {
        this._customStoreStyles = {};
        this._hideAllLayers();

        if (this.check()) {
            this._loadLayers();
        }
    }

    purge () {
        this._hideAllLayers();
    }

    validToBuildServiceArea() {
        const currentMap = worldMap.getCurrent();
        const mapId = currentMap._id;

        if (!this._isNetworkActive(mapId)) {
            notify(NOTIFY_TYPE.LOG, 'A camada de Rede precisa estar ativa para carregar as áreas de influência.');
            return false;
        }

        const options = this._getServiceAreaOptions();

        if (!options.showRadiusReal && !options.showRadiusTheoretical) {
            notify(NOTIFY_TYPE.LOG, 'Selecione ao menos um tipo de área (Real ou Potencial).');
            return false;
        }

        return true;
    }
}

export default ServiceAreaAfterSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/AfterSales/ServiceAreaAfterSalesControl.js